/**
 * NX-2802 — config-driven signal bands: does this number indicate success or an issue?
 *
 * Papa (2026-08-26): "it's hard to tell if any of the metrics indicate an issue or success.
 * visually they all look the same." A banded metric renders its value with a good/attention tone
 * and a small badge; the thresholds live HERE, keyed by the rendered label, never at render sites
 * (configuration over code).
 *
 * Honesty rules (ADR-010):
 *  - Every band's basis is DISCLOSED — it is appended to the metric's visible explanation, so a
 *    tone is never an unexplained judgment. DORA-derived bands cite the benchmark; the rest are
 *    labeled heuristics and are Papa-tunable in this file.
 *  - A value between the two thresholds is NEUTRAL (no tone) — the mid-zone is genuinely
 *    inconclusive and pretending otherwise would fabricate certainty.
 *  - Metrics with no defensible band simply have no entry and render exactly as before.
 *  - N/A values are never banded (the honest states never reach this code).
 *
 * Every entry documents the RAW UNIT its thresholds assume; call sites must pass that unit.
 */

export type SignalTone = "good" | "attention";

export interface SignalBand {
  /** lower-better: raw <= good → good, raw >= attention → attention.
   *  higher-better: raw >= good → good, raw <= attention → attention.
   *  near-target: |raw - target| <= good → good, >= attention → attention.
   *  healthy-range: good <= raw <= goodMax → good, raw <= attention → attention, above goodMax →
   *  neutral — for ratios where an implausibly high reading signals thin data, not excellence. */
  readonly kind: "lower-better" | "higher-better" | "near-target" | "healthy-range";
  readonly good: number;
  readonly attention: number;
  readonly target?: number;
  readonly goodMax?: number;
  /** Disclosed provenance of the thresholds — rendered into the metric's explanation. */
  readonly basis: string;
}

export const METRIC_SIGNALS: Record<string, SignalBand> = {
  // raw unit: percent (0–100)
  "Change failure rate": {
    kind: "lower-better",
    good: 15,
    attention: 30,
    basis:
      "What counts as good here: ≤15% (DORA benchmark for high performers); ≥30% needs attention.",
  },
  // raw unit: deploys per day
  "Deploy frequency": {
    kind: "higher-better",
    good: 1,
    attention: 0.143,
    basis:
      "What counts as good here: daily or better (DORA benchmark); less than weekly needs attention.",
  },
  // raw unit: percent (0–100). NX-2854: a ~99% reading is usually THIN WAIT DATA (few observed
  // wait intervals), not genuinely waitless flow — so the good zone is a range, and implausibly
  // high readings fall back to neutral rather than earning an unearned green badge.
  "Flow efficiency": {
    kind: "healthy-range",
    good: 40,
    goodMax: 85,
    attention: 15,
    basis:
      "What counts as good here: 40–85% active work (industry heuristic); ≤15% suggests heavy waiting and needs attention. Readings above 85% usually mean wait times were rarely observed (thin data), so no judgment is made.",
  },
  // raw unit: mean ratio of actual to estimated (1.0 = perfectly calibrated)
  "Estimate accuracy": {
    kind: "near-target",
    target: 1,
    good: 0.15,
    attention: 0.5,
    basis:
      "What counts as good here: within ±15% of 1.0× (heuristic); off by half or more needs attention.",
  },
  // raw unit: hours of mean review wait
  "Review wait": {
    kind: "lower-better",
    good: 4,
    attention: 24,
    basis:
      "What counts as good here: under ~4h mean wait (heuristic); a day or more needs attention.",
  },
};

/** The tone for a raw value under its label's band — null = neutral mid-zone or no band. */
export function signalToneFor(label: string, raw: number): SignalTone | null {
  const band = METRIC_SIGNALS[label];
  if (band === undefined || !Number.isFinite(raw)) return null;
  if (band.kind === "near-target") {
    const dist = Math.abs(raw - (band.target ?? 0));
    return dist <= band.good ? "good" : dist >= band.attention ? "attention" : null;
  }
  if (band.kind === "lower-better") {
    return raw <= band.good ? "good" : raw >= band.attention ? "attention" : null;
  }
  if (band.kind === "healthy-range") {
    if (raw <= band.attention) return "attention";
    return raw >= band.good && raw <= (band.goodMax ?? Infinity) ? "good" : null;
  }
  return raw >= band.good ? "good" : raw <= band.attention ? "attention" : null;
}

/** The disclosed basis line for a label's band, if one exists. */
export function signalBasisFor(label: string): string | undefined {
  return METRIC_SIGNALS[label]?.basis;
}
